import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ChevronDown, ChevronRight, Building, Phone, Columns3, Share2,
  Mail, Copyright, Trash2, Plus, GripVertical, Link2, Type, X
} from 'lucide-react';

// ============================================================================
// Collapsible Section (defined outside — stable identity)
// ============================================================================
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
          padding: '12px 16px', background: 'none', border: 'none',
          cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#111827',
          textAlign: 'left',
        }}
      >
        <Icon size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{title}</span>
        {open ? <ChevronDown size={14} style={{ color: '#9ca3af' }} /> : <ChevronRight size={14} style={{ color: '#9ca3af' }} />}
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Reusable form controls (defined outside — stable identity)
// ============================================================================
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' };
const inputStyle = {
  width: '100%', padding: '7px 10px', fontSize: '12px', border: '1px solid #d1d5db',
  borderRadius: '6px', outline: 'none', color: '#111827', background: '#fff',
  boxSizing: 'border-box',
};
const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: '56px', fontFamily: 'inherit' };

const Field = ({ label, value, onChange, placeholder, type = 'text', textarea = false }) => (
  <div style={{ marginBottom: '10px' }}>
    <label style={labelStyle}>{label}</label>
    {textarea ? (
      <textarea
        style={textareaStyle}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    ) : (
      <input
        type={type}
        style={inputStyle}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    )}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
    <span style={{ fontSize: '12px', color: '#374151' }}>{label}</span>
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: '36px', height: '20px', borderRadius: '10px', border: 'none',
        background: checked ? '#4f46e5' : '#d1d5db', cursor: 'pointer',
        position: 'relative', transition: 'background 0.2s',
      }}
    >
      <div style={{
        width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
        position: 'absolute', top: '2px', left: checked ? '18px' : '2px',
        transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </button>
  </div>
);

// ============================================================================
// Deep set helper — sets a nested value by dot-separated path
// ============================================================================
function deepSet(obj, path, value) {
  const next = JSON.parse(JSON.stringify(obj));
  const keys = path.split('.');
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] === undefined || cur[k] === null) cur[k] = isNaN(keys[i + 1]) ? {} : [];
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
}

// ============================================================================
// FooterConfigPanel
// ============================================================================
export default function FooterConfigPanel({ attributes = {}, onChange, onClose }) {
  // Local state for immediate input responsiveness — avoids stale closure issues
  const [local, setLocal] = useState(() => ({ ...attributes }));

  // Refs for stable callbacks (never stale)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  // Sync from parent only when no pending local changes
  useEffect(() => {
    if (!timerRef.current) {
      setLocal(prev => ({ ...prev, ...attributes }));
    }
  }, [attributes]);

  // Debounced parent notification
  const notifyParent = useCallback((newAttrs) => {
    pendingRef.current = newAttrs;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) onChangeRef.current(pending);
    }, 150);
  }, []);

  // Flush pending changes (on close or unmount)
  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (pendingRef.current) {
        onChangeRef.current(pendingRef.current);
        pendingRef.current = null;
      }
    }
  }, []);

  useEffect(() => () => flush(), [flush]);

  // Nested path update — works on local state, notifies parent with debounce
  const update = useCallback((path, value) => {
    setLocal(prev => {
      const next = deepSet(prev, path, value);
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  // Derived values from local state
  const brand = local.brand || {};
  const contact = local.contact || {};
  const columns = local.columns || [];
  const social = local.social || {};
  const newsletter = local.newsletter || {};
  const copyrightData = local.copyright || {};

  // Column helpers — all use functional state updates
  const updateColumn = useCallback((colIndex, field, value) => {
    setLocal(prev => {
      const newCols = JSON.parse(JSON.stringify(prev.columns || []));
      newCols[colIndex][field] = value;
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const addColumn = useCallback(() => {
    setLocal(prev => {
      const newCols = [...JSON.parse(JSON.stringify(prev.columns || [])), {
        id: Date.now().toString(),
        title: 'New Column',
        links: [{ label: 'Link', url: '/' }],
      }];
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const removeColumn = useCallback((colIndex) => {
    setLocal(prev => {
      const newCols = JSON.parse(JSON.stringify(prev.columns || []));
      newCols.splice(colIndex, 1);
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const addLink = useCallback((colIndex) => {
    setLocal(prev => {
      const newCols = JSON.parse(JSON.stringify(prev.columns || []));
      if (!newCols[colIndex].links) newCols[colIndex].links = [];
      newCols[colIndex].links.push({ label: 'New Link', url: '/' });
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const removeLink = useCallback((colIndex, linkIndex) => {
    setLocal(prev => {
      const newCols = JSON.parse(JSON.stringify(prev.columns || []));
      newCols[colIndex].links.splice(linkIndex, 1);
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const updateLink = useCallback((colIndex, linkIndex, field, value) => {
    setLocal(prev => {
      const newCols = JSON.parse(JSON.stringify(prev.columns || []));
      newCols[colIndex].links[linkIndex][field] = value;
      const next = { ...prev, columns: newCols };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  // Handle close — flush pending changes first
  const handleClose = useCallback(() => {
    flush();
    onClose?.();
  }, [flush, onClose]);

  return (
    <div style={{
      width: '320px', height: '100%', background: '#fff',
      borderLeft: '1px solid #e5e7eb', overflowY: 'auto',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* Panel header */}
      <div style={{
        padding: '12px 16px', background: '#1e293b', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Building size={16} />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Footer Settings</span>
        </div>
        {handleClose && (
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', borderRadius: '4px',
          }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* ── Brand ── */}
      <Section title="Brand" icon={Type} defaultOpen={true}>
        <Field
          label="Brand Name"
          value={brand.name}
          onChange={(v) => update('brand.name', v)}
          placeholder="Your Brand Name"
        />
        <Field
          label="Description"
          value={brand.description}
          onChange={(v) => update('brand.description', v)}
          placeholder="Short description about your brand"
          textarea
        />
        <Field
          label="Logo Image Link"
          value={brand.logo?.imageUrl}
          onChange={(v) => update('brand.logo.imageUrl', v)}
          placeholder="Paste your logo image link here"
        />
      </Section>

      {/* ── Contact ── */}
      <Section title="Contact Info" icon={Phone}>
        <Field
          label="Email"
          value={contact.email}
          onChange={(v) => update('contact.email', v)}
          placeholder="hello@example.com"
          type="email"
        />
        <Field
          label="Phone"
          value={contact.phone}
          onChange={(v) => update('contact.phone', v)}
          placeholder="+91 98765 43210"
        />
        <Field
          label="Address"
          value={contact.address}
          onChange={(v) => update('contact.address', v)}
          placeholder="123 Fashion Street, Mumbai"
        />
      </Section>

      {/* ── Link Columns ── */}
      <Section title="Link Columns" icon={Columns3}>
        {columns.map((col, colIndex) => (
          <div key={col.id || colIndex} style={{
            marginBottom: '14px', padding: '10px',
            background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
          }}>
            {/* Column header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <GripVertical size={12} style={{ color: '#d1d5db', flexShrink: 0 }} />
              <input
                style={{ ...inputStyle, fontWeight: 600, fontSize: '12px', flex: 1 }}
                value={col.title || ''}
                onChange={(e) => updateColumn(colIndex, 'title', e.target.value)}
                placeholder="Column Title"
              />
              <button
                onClick={() => removeColumn(colIndex)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '4px', borderRadius: '4px', color: '#ef4444',
                  display: 'flex', alignItems: 'center',
                }}
                title="Remove column"
              >
                <Trash2 size={13} />
              </button>
            </div>

            {/* Links in this column */}
            {(col.links || []).map((link, linkIndex) => (
              <div key={linkIndex} style={{
                display: 'flex', gap: '4px', marginBottom: '6px', alignItems: 'center',
              }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <input
                    style={{ ...inputStyle, fontSize: '11px', padding: '5px 8px' }}
                    value={link.label || ''}
                    onChange={(e) => updateLink(colIndex, linkIndex, 'label', e.target.value)}
                    placeholder="Label"
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Link2 size={10} style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <select
                      style={{ ...inputStyle, fontSize: '10px', padding: '4px 6px', color: '#6b7280' }}
                      value={link.url || '/'}
                      onChange={(e) => updateLink(colIndex, linkIndex, 'url', e.target.value)}
                    >
                      <option value="/">Home</option>
                      <option value="/shop">Shop</option>
                      <option value="/categories">Categories</option>
                      <option value="/collections">Collections</option>
                      <option value="/new-arrivals">New Arrivals</option>
                      <option value="/sale">Sale</option>
                      <option value="/bestsellers">Bestsellers</option>
                      <option value="/about">About</option>
                      <option value="/contact">Contact</option>
                      <option value="/cart">Cart</option>
                      <option value="/login_page">Login</option>
                      <option value="/previous_order">My Orders</option>
                      <option value="/privacy_policy">Privacy Policy</option>
                      <option value="/shipping_delivery">Shipping & Returns</option>
                      <option value="/terms_conditions">Terms & Conditions</option>
                      <option value="/faq">FAQ</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => removeLink(colIndex, linkIndex)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '4px', color: '#d1d5db', display: 'flex', alignItems: 'center',
                  }}
                  title="Remove link"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}

            {/* Add link button */}
            <button
              onClick={() => addLink(colIndex)}
              style={{
                width: '100%', padding: '5px', fontSize: '11px', color: '#4f46e5',
                background: 'none', border: '1px dashed #c7d2fe', borderRadius: '4px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              }}
            >
              <Plus size={11} /> Add Link
            </button>
          </div>
        ))}

        {/* Add column button */}
        <button
          onClick={addColumn}
          style={{
            width: '100%', padding: '8px', fontSize: '12px', fontWeight: 500,
            color: '#4f46e5', background: '#eef2ff', border: '1px dashed #a5b4fc',
            borderRadius: '6px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}
        >
          <Plus size={13} /> Add Column
        </button>
      </Section>

      {/* ── Social Media ── */}
      <Section title="Social Media" icon={Share2}>
        <Field
          label="Facebook"
          value={social.facebook}
          onChange={(v) => update('social.facebook', v)}
          placeholder="https://facebook.com/your-page"
        />
        <Field
          label="Instagram"
          value={social.instagram}
          onChange={(v) => update('social.instagram', v)}
          placeholder="https://instagram.com/your-handle"
        />
        <Field
          label="Twitter / X"
          value={social.twitter}
          onChange={(v) => update('social.twitter', v)}
          placeholder="https://twitter.com/your-handle"
        />
        <Field
          label="LinkedIn"
          value={social.linkedin}
          onChange={(v) => update('social.linkedin', v)}
          placeholder="https://linkedin.com/company/your-co"
        />
        <Field
          label="YouTube"
          value={social.youtube}
          onChange={(v) => update('social.youtube', v)}
          placeholder="https://youtube.com/@your-channel"
        />
        <Field
          label="Pinterest"
          value={social.pinterest}
          onChange={(v) => update('social.pinterest', v)}
          placeholder="https://pinterest.com/your-profile"
        />
      </Section>

      {/* ── Newsletter ── */}
      <Section title="Newsletter" icon={Mail}>
        <Toggle
          label="Show Newsletter"
          checked={newsletter.enabled !== false}
          onChange={(v) => update('newsletter.enabled', v)}
        />
        {newsletter.enabled !== false && (
          <>
            <Field
              label="Title"
              value={newsletter.title}
              onChange={(v) => update('newsletter.title', v)}
              placeholder="Subscribe to Our Newsletter"
            />
            <Field
              label="Subtitle"
              value={newsletter.subtitle}
              onChange={(v) => update('newsletter.subtitle', v)}
              placeholder="Get the latest updates and offers"
            />
            <Field
              label="Placeholder Text"
              value={newsletter.placeholder}
              onChange={(v) => update('newsletter.placeholder', v)}
              placeholder="Enter your email"
            />
          </>
        )}
      </Section>

      {/* ── Copyright ── */}
      <Section title="Copyright" icon={Copyright}>
        <Field
          label="Copyright Text"
          value={copyrightData.text}
          onChange={(v) => update('copyright.text', v)}
          placeholder="Brand Name"
        />
        <Field
          label="Year"
          value={copyrightData.year}
          onChange={(v) => update('copyright.year', v)}
          placeholder={new Date().getFullYear().toString()}
        />
        <Toggle
          label="Show Payment Icons"
          checked={copyrightData.showPaymentIcons !== false}
          onChange={(v) => update('copyright.showPaymentIcons', v)}
        />
      </Section>
    </div>
  );
}
